import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Modal, Button } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { getVenueSuppliers } from '../features/VenueSuppliersSlice'
import Loading from './styled/Loading'
import { API } from 'src/utils/interceptor'
import {
  discardUnconfirmedUploadedInvoice,
  discardUnconfirmedUploadedInvoices,
  enrichUploadedInvoice,
  getUploadedInvoices,
  enrichUploadedInvoices,
} from '../features/NewInvoicesSlice'
import PlanLimitModal from 'src/screens/subscriptions/PlanLimitModal'
import { notifyInvoiceUploadPaywallSales } from 'src/utils/invoiceUploadPaywallNotify'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import { setFeatureLimit } from 'src/features/VenueSlice'
import { LockOutlined } from '@ant-design/icons'
import {
  UPLOAD_PLAN_LIMIT_COPY,
  getInvoiceUploadUsageSummary,
} from 'src/utils/invoiceUploadQueuePolish'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import { getPostConfirmNextStepNotification } from 'src/utils/invoiceUploadLoopClose'
import promptUploadPickPreflight from './invoices/promptUploadPickPreflight'
import {
  MAX_INVOICE_FILE_BYTES,
  uploadInvoicesIndividually,
} from '../utils/invoiceUploadRequests'
import promptUploadDuplicateWarn from './invoices/promptUploadDuplicateWarn'
import ReviewUploadsPanel from './invoices/ReviewUploadsPanel'
import IncompleteUploadsTray from './invoices/IncompleteUploadsTray'
import {
  applyInvoiceTotalEstimate,
  applyManualPurchaseOrderTouchToUploadFile,
  applyOcrSuggestionsToUploadFile,
  applyPurchaseOrderUpdate,
  applyUploadPrefillToFile,
  buildEmptyUploadFileFields,
  buildEnrichPayloadFromUploadFile,
  CREATE_SUPPLIER_FROM_UPLOAD_COPY,
  INVOICE_TOTAL_SOURCE,
  mergeOcrResultPreservingUploadFileEdits,
  touchUploadFileField,
} from '../utils/invoiceUploadHelpers'
import { createManualVenueSupplierFromUpload } from '../utils/createManualVenueSupplierFromUploadApi'
import {
  UPLOAD_DRAFT_STATUS,
  UPLOAD_QUEUE_COPY,
  createUploadSessionDrafts,
  formatUploadQueueProgressLine,
  remainingQuotaAfterDiscardRestore,
  resolveEffectiveVenuePlan,
  resolveUploadRemainingQuota,
  takeFirstNPickerFiles,
  transitionUploadDraft,
} from '../utils/invoiceUploadQueueHelpers'
import {
  attachInvoiceIdsToUploadDrafts,
  INVOICE_OCR_REQUEST_TIMEOUT_MS,
  isRetryableInvoiceOcrResult,
  markMissingInvoiceIdDraftsFailed,
  pairCreatedInvoicesToUploadItems,
  requestOcrWithRetries,
  runOcrPool,
  shouldAutoEnrichAfterOcr,
} from '../utils/invoiceUploadOcrPool'
import {
  isSparseUploadOcrFile,
  mergeSameInvoiceSessionPages,
} from '../utils/invoiceUploadSameInvoicePages'
import {
  canConfirmUploadReviewFocus,
  canConfirmAllUploadReview,
  findUploadReviewDraft,
  findUploadReviewFile,
  getConfirmAllUploadReviewLabel,
  getUploadReviewRemoveOutcome,
  listUploadReviewConfirmableItems,
  markUploadDraftConfirmed,
  markUploadDraftConfirming,
  markUploadDraftDiscarded,
  pickFocusAfterUploadReviewAction,
} from '../utils/invoiceUploadReviewHelpers'
import {
  INCOMPLETE_UPLOAD_KIND,
  removeIncompleteUploads,
} from '../utils/invoiceUploadIncompleteStorage'
import {
  buildResumeSessionFromIncompleteList,
  discardSparseAndPersistIncompleteSession,
  listIncompleteUploadsForVenue,
  runIncompleteUploadsOnLoadCleanup,
} from '../utils/invoiceUploadIncompleteCleanup'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'

/** Survives remounts — incomplete cleanup must not re-fire getUploadedInvoices. */
const incompleteCleanupRanForVenue = new Set()

function countSuccessfulDiscardResults(results, fallbackCount) {
  if (!Array.isArray(results)) return fallbackCount
  return results.filter(result => result != null && result.success !== false)
    .length
}

function AddInvoices({
  venueId,
  setAttachInvoices,
  isNotDispatchInvoices = false,
  uploadPrefill = null,
  onConfirmed = null,
  /**
   * Optional gate before enrich/confirm.
   * Return true to proceed, false to abort, or 'use_existing' to discard
   * the draft upload (GR duplicate nudge → Link existing).
   */
  beforeConfirmUpload = null,
  triggerLabel = 'Add Invoices / Credit Notes',
  /** When true, show Incomplete uploads tray under the trigger (Invoices). */
  showIncompleteTray = false,
  onIncompleteChange = null,
  /**
   * When set (Invoices tab), refresh via this instead of fetching the full
   * invoicesInfo library page-by-page — keeps the table on current-page fetch.
   */
  onRefreshInvoices = null,
  /** Increment to open the file picker (Guided Success first-try CTA). */
  requestOpenUpload = 0,
}) {
  const dispatch = useDispatch()

  const refreshInvoiceList = async () => {
    if (typeof onRefreshInvoices === 'function') {
      await onRefreshInvoices()
      return
    }
    if (!isNotDispatchInvoices) {
      await dispatch(getUploadedInvoices(venueId))
    }
  }

  const [selectedFiles, setSelectedFiles] = useState([])
  const selectedUploadFilesRef = useRef([])
  const commitSelectedUploadFiles = useCallback(updater => {
    setSelectedFiles(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      selectedUploadFilesRef.current = next
      return next
    })
  }, [])
  const [uploadDrafts, setUploadDrafts] = useState([])
  const [previewZoomByDraftId, setPreviewZoomByDraftId] = useState({})
  const [confirmModal, setConfirmModal] = useState(false)
  const [focusId, setFocusId] = useState(null)
  const [userPickedFocus, setUserPickedFocus] = useState(false)
  const fileInputRef = useRef(null)
  const ocrCancelRef = useRef(false)
  const reviewLaterRef = useRef(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [ocrActive, setOcrActive] = useState(false)
  const [discardLoading, setDiscardLoading] = useState(false)
  const [ocrBanner, setOcrBanner] = useState('')
  const [incompleteCount, setIncompleteCount] = useState(0)
  const [cleanupBanner, setCleanupBanner] = useState('')
  const venue = useSelector(state => state.venue?.venue)
  const usageLimits = venue?.usageLimits
  const features = usageLimits?.features || {}
  const currentPlan = resolveEffectiveVenuePlan({
    newPricing: venue?.newPricing,
    usageLimits,
  })
  const { Upload_Invoices_Dashboard } = features || {}
  const remainingUploadQuota = resolveUploadRemainingQuota({
    currentPlan,
    uploadInvoicesDashboard: Upload_Invoices_Dashboard,
    usageLimitsPlan: usageLimits?.currentPlan,
  })
  const invoiceUploadUsage = getInvoiceUploadUsageSummary({
    currentPlan,
    remainingQuota: remainingUploadQuota,
  })
  const isUploadLimitReached = Boolean(invoiceUploadUsage.isAtLimit)
  const [showLimitModal, setShowLimitModal] = useState(false)

  const restoreLocalUploadQuota = restoredCount => {
    if (remainingUploadQuota == null) return
    const restored = Math.max(0, Number(restoredCount) || 0)
    if (!restored) return
    const nextRemaining = remainingQuotaAfterDiscardRestore({
      remainingQuota: remainingUploadQuota,
      restoredCount: restored,
      currentPlan,
    })
    if (nextRemaining == null) return
    dispatch(
      setFeatureLimit({
        feature: 'Upload_Invoices_Dashboard',
        limit: nextRemaining,
      }),
    )
    dispatch(
      setFeatureLimit({
        feature: 'Upload_Invoices_App',
        limit: nextRemaining,
      }),
    )
  }

  // Single definition — used by trigger, guided CTA, and quota gates.
  const openInvoiceUploadPaywall = () => {
    notifyInvoiceUploadPaywallSales({
      venueId,
      source: 'client',
      platform: 'dashboard',
      remainingLimit: Upload_Invoices_Dashboard?.limit ?? 0,
    })
    setShowLimitModal(true)
  }

  useEffect(() => {
    if (!requestOpenUpload) return
    if (isUploadLimitReached || ocrActive || uploading) {
      // Guided CTA tried to open the picker while a session is busy / at limit.
      if (isUploadLimitReached) {
        openInvoiceUploadPaywall()
      } else if (uploading) {
        RejectedNotification({
          message: 'Upload already in progress',
          description:
            'Review uploads is still uploading your files. Wait for that to finish before adding more.',
        })
      } else if (ocrActive) {
        RejectedNotification({
          message: 'Still reading your invoices',
          description:
            'OCR is still running on the current batch. Wait for it to finish, or continue in Review uploads if it is open.',
        })
      }
      return
    }
    fileInputRef.current?.click()
  }, [requestOpenUpload, isUploadLimitReached, ocrActive, uploading])

  const venueSuppliers = useSelector(state =>
    state.venueSuppliers.venueSuppliers.filter(
      venueSupplier => venueSupplier.isActive,
    ),
  )
  const uploadedInvoices = useSelector(
    state => state.newInvoices.uploadedInvoices,
  )
  const user = useSelector(state => state.auth)
  const username = user?.account?.name
  const isAdmin = user?.account?.isAdmin
  const venueCount = Array.isArray(user?.account?.venues)
    ? user.account.venues.length
    : 0

  const venueTitle = useSelector(state => state.venue.venue.title)
  const [initialSuppleirs, setInitialSuppilers] = useState([])

  const isLoading = useSelector(state => state.venueSuppliers.isLoading)
  const resolvedPrefill = uploadPrefill?.upload ? uploadPrefill : null

  const refreshIncompleteCount = (library = uploadedInvoices) => {
    const { count } = listIncompleteUploadsForVenue({
      venueId,
      libraryInvoices: library || [],
    })
    setIncompleteCount(count)
    if (typeof onIncompleteChange === 'function') {
      onIncompleteChange(count)
    }
  }

  useEffect(() => {
    if (!venueId) {
      setIncompleteCount(0)
      return
    }
    refreshIncompleteCount(uploadedInvoices || [])
  }, [venueId, uploadedInvoices])

  // Chunk U5 — on-load 7-day cleanup (once per venue; module-scoped so a
  // remount cannot re-trigger a full invoicesInfo library walk).
  useEffect(() => {
    if (!showIncompleteTray || !venueId) return
    const venueKey = String(venueId)
    if (incompleteCleanupRanForVenue.has(venueKey)) return

    let cancelled = false
    const run = async () => {
      incompleteCleanupRanForVenue.add(venueKey)
      // Prefer whatever library is already in Redux. Do NOT call
      // getUploadedInvoices here when onRefreshInvoices is provided — that
      // walks every invoicesInfo page and reintroduces the timeout issue.
      let library = uploadedInvoices || []
      if (
        !isNotDispatchInvoices &&
        typeof onRefreshInvoices !== 'function' &&
        !library.length
      ) {
        const refreshed = await dispatch(getUploadedInvoices(venueId))
        if (Array.isArray(refreshed)) library = refreshed
      }
      if (cancelled) return

      const result = await runIncompleteUploadsOnLoadCleanup({
        venueId,
        libraryInvoices: library,
        discardInvoiceIds: async ids => {
          const results = await discardUnconfirmedUploadedInvoices(
            ids.map(invoiceId => ({ invoiceId })),
            venueId,
          )
          restoreLocalUploadQuota(
            countSuccessfulDiscardResults(results, ids.length),
          )
        },
      })
      if (cancelled) return
      setIncompleteCount(result.count)
      if (result.banner) setCleanupBanner(result.banner)
      if (typeof onIncompleteChange === 'function') {
        onIncompleteChange(result.count)
      }
      if (result.discardedIds.length && !isNotDispatchInvoices) {
        await refreshInvoiceList()
      }
    }

    run().catch(error => {
      console.error('Incomplete upload cleanup failed', error)
      incompleteCleanupRanForVenue.delete(venueKey)
    })

    return () => {
      cancelled = true
    }
  }, [venueId, showIncompleteTray, isNotDispatchInvoices, dispatch])

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetConfirmModalState = () => {
    setConfirmModal(false)
    selectedUploadFilesRef.current = []
    setSelectedFiles([])
    setUploadDrafts([])
    setOcrBanner('')
    setPreviewZoomByDraftId({})
    setFocusId(null)
    setUserPickedFocus(false)
  }

  /** Close Review uploads and tell the user why (failures used to look silent). */
  const closeConfirmModalWithFailure = ({ message, description }) => {
    resetConfirmModalState()
    clearFileInput()
    RejectedNotification({
      message: message || 'Review uploads closed',
      description:
        description ||
        'The upload window was closed because something went wrong. Please try again.',
    })
  }

  const notifyUploadTriggerBlocked = () => {
    if (isUploadLimitReached) {
      openInvoiceUploadPaywall()
      return
    }
    if (uploading) {
      RejectedNotification({
        message: 'Upload already in progress',
        description:
          'Review uploads is still uploading your files. Wait for that to finish before adding more.',
      })
      return
    }
    if (ocrActive) {
      RejectedNotification({
        message: 'Still reading your invoices',
        description:
          'OCR is still running on the current batch. Wait for it to finish, or open Review uploads if it is already open.',
      })
    }
  }

  const finishSession = async () => {
    resetConfirmModalState()
    if (!isNotDispatchInvoices) {
      await refreshInvoiceList()
    }
    // L1 — close the loop: where to go next (match when ready).
    SuccessNotification(getPostConfirmNextStepNotification())
    if (typeof onConfirmed === 'function') {
      await onConfirmed()
    }
  }

  const runOcrForDraft = async (draft, file, suppliersForMatch = []) => {
    const invoiceId = draft?.invoiceId || file?.invoiceId
    if (!invoiceId) {
      return {
        ok: false,
        error: 'Upload did not return an invoice id',
        file: applyUploadPrefillToFile(
          {
            ...file,
            ocrStatus: 'failed',
            ocrError: 'Upload did not return an invoice id',
            ocrSuggested: null,
            ocrWarnings: ['upload_missing_id'],
          },
          resolvedPrefill,
        ),
      }
    }

    const requestOcr = async () => {
      const ocrResponse = await API.post('api2', `invoices/${invoiceId}/ocr`, {
        body: {},
        timeout: INVOICE_OCR_REQUEST_TIMEOUT_MS,
      })
      const ocrData = {
        ...(ocrResponse?.data || {}),
        invoiceId,
      }
      if (isRetryableInvoiceOcrResult(ocrData)) {
        throw new Error(ocrData.error || 'Invoice OCR failed')
      }
      const nextFile = applyUploadPrefillToFile(
        applyOcrSuggestionsToUploadFile(
          { ...file, invoiceId },
          ocrData,
          suppliersForMatch,
        ),
        resolvedPrefill,
      )
      return { ok: true, file: nextFile }
    }

    const failResult = error => ({
      ok: false,
      error:
        error?.response?.data?.message ||
        error?.message ||
        'Invoice OCR failed',
      file: applyUploadPrefillToFile(
        {
          ...file,
          invoiceId,
          ocrStatus: 'failed',
          ocrSuggested: null,
          ocrWarnings: ['ocr_request_failed'],
          ocrError:
            error?.response?.data?.message ||
            error?.message ||
            'Invoice OCR failed',
        },
        resolvedPrefill,
      ),
    })

    try {
      return await requestOcrWithRetries({
        request: requestOcr,
        isCancelled: () => ocrCancelRef.current,
      })
    } catch (error) {
      console.error('Invoice OCR failed:', error)
      return failResult(error)
    }
  }

  const runUploadAndOcr = async (files, drafts, suppliersForMatch = []) => {
    ocrCancelRef.current = false
    reviewLaterRef.current = false
    setUploading(true)
    setOcrActive(false)
    setOcrBanner('Uploading invoices / credit notes…')
    try {
      const uploadBody = {
        venue: venueId,
        venueTitle: venueTitle,
        images: files.map(file => ({
          filename: file.filename,
          originalFilename: file.filename,
          image: file.image,
          documentType: file.documentType || 'invoice',
          comment: '',
          purchaseOrderNumber: file.purchaseOrderNumber || '',
        })),
        isDashboardUpload: true,
        username,
        userType: isAdmin ? 'Admin' : 'Customer',
      }

      const { invoices: uploadedByIndex, errors: uploadErrors } =
        await uploadInvoicesIndividually(uploadBody, body =>
          API.post('api', `venue/${venueId}/invoices_photos_to_s3`, { body }),
        )
      const newInvoices = uploadedByIndex.filter(invoice => invoice?._id)
      if (!newInvoices.length) {
        throw new Error(uploadErrors.find(Boolean) || 'Upload failed')
      }
      if (uploadErrors.some(Boolean)) {
        RejectedNotification({
          message: 'Some invoices could not be uploaded',
          description: uploadErrors.filter(Boolean).join(' '),
        })
      }
      // Keep placeholders so failed files (including duplicate filenames) cannot
      // take another file's id during review pairing.
      const uploadResults = uploadedByIndex.map((invoice, index) => ({
        ...invoice,
        originalFilename: files[index].filename,
      }))
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
        viaServer: true,
      })

      if (venueId && newInvoices.length > 0) {
        emitGrowthEngineGuidedLaneProgress(
          venueId,
          GROWTH_ENGINE_GUIDED_LANE_REASONS.INVOICE_UPLOADED,
        )
      }
      if (setAttachInvoices) {
        setAttachInvoices(curr => [
          ...newInvoices.map(({ _id }) => _id),
          ...curr,
        ])
      }

      // Keep Redux quota in sync with server decrement; notify CS when allowance hits 100%.
      if (remainingUploadQuota != null && newInvoices.length > 0) {
        const nextRemaining = Math.max(
          0,
          remainingUploadQuota - newInvoices.length,
        )
        dispatch(
          setFeatureLimit({
            feature: 'Upload_Invoices_Dashboard',
            limit: nextRemaining,
          }),
        )
        dispatch(
          setFeatureLimit({
            feature: 'Upload_Invoices_App',
            limit: nextRemaining,
          }),
        )
        if (nextRemaining <= 0) {
          notifyInvoiceUploadPaywallSales({
            venueId,
            source: 'client',
            platform: 'dashboard',
            remainingLimit: 0,
            uploadCount: newInvoices.length,
          })
        }
      }

      let nextDrafts = attachInvoiceIdsToUploadDrafts(drafts, uploadResults)
      nextDrafts = markMissingInvoiceIdDraftsFailed(nextDrafts)

      const pairedInvoices = pairCreatedInvoicesToUploadItems(
        files,
        uploadResults,
        file => file?.filename,
      )

      const filesWithIds = files.map((file, index) => {
        const created = pairedInvoices[index]
        const invoiceId = created?._id ? String(created._id) : null
        const draft = nextDrafts[index]
        if (!invoiceId) {
          return applyUploadPrefillToFile(
            {
              ...file,
              draftId: draft?.id,
              pickIndex: index,
              invoiceId: null,
              ocrStatus: 'failed',
              ocrError: 'Upload did not return an invoice id',
              ocrSuggested: null,
              ocrWarnings: ['upload_missing_id'],
            },
            resolvedPrefill,
          )
        }
        return {
          ...file,
          draftId: draft?.id,
          pickIndex: index,
          invoiceId,
          ocrStatus: 'pending',
        }
      })

      commitSelectedUploadFiles(filesWithIds)
      setUploadDrafts(nextDrafts)
      setFocusId(pickFocusAfterUploadReviewAction(nextDrafts, null))
      setUserPickedFocus(false)
      setUploading(false)
      setOcrActive(true)
      setOcrBanner(formatUploadQueueProgressLine(nextDrafts))

      const filesByDraftId = new Map(
        filesWithIds.map(file => [String(file.draftId), file]),
      )
      let sessionFilesSnapshot = [...filesWithIds]

      const applyOcrResultToSessionFile = (draftId, ocrFile) => {
        const key = String(draftId)
        let mergedSnapshot = null
        commitSelectedUploadFiles(prev => {
          mergedSnapshot = prev.map(file => {
            if (String(file.draftId) !== key) return file
            const merged = mergeOcrResultPreservingUploadFileEdits(
              file,
              ocrFile,
            )
            filesByDraftId.set(key, merged)
            return merged
          })
          sessionFilesSnapshot = mergedSnapshot
          return mergedSnapshot
        })
        return mergedSnapshot
      }

      const poolResult = await runOcrPool({
        initialDrafts: nextDrafts,
        isCancelled: () => ocrCancelRef.current,
        onDraftsChange: (updated, { progressLine }) => {
          setUploadDrafts(updated)
          if (progressLine) setOcrBanner(progressLine)
        },
        processItem: async draft => {
          const file =
            selectedUploadFilesRef.current.find(
              f => String(f.draftId) === String(draft.id),
            ) || filesByDraftId.get(String(draft.id))
          const result = await runOcrForDraft(draft, file, suppliersForMatch)
          let mergedSessionFiles = null
          if (result.file) {
            mergedSessionFiles = applyOcrResultToSessionFile(
              draft.id,
              result.file,
            )
          }
          if (result.ok) {
            const enrichedFile =
              mergedSessionFiles?.find(
                f => String(f.draftId) === String(draft.id),
              ) ||
              sessionFilesSnapshot.find(
                f => String(f.draftId) === String(draft.id),
              ) ||
              result.file ||
              file
            if (shouldAutoEnrichAfterOcr(enrichedFile)) {
              try {
                await enrichUploadedInvoices([
                  {
                    invoiceId: enrichedFile.invoiceId,
                    body: {
                      ...buildEnrichPayloadFromUploadFile(enrichedFile),
                      supplierConfirmedByUser: false,
                      uploadConfirmed: false,
                    },
                  },
                ])
              } catch (error) {
                console.error('Auto-enrich after OCR failed:', error)
              }
            }
            return { ok: true, patch: { invoiceId: draft.invoiceId } }
          }
          return {
            ok: false,
            error: result.error,
            patch: { invoiceId: draft.invoiceId },
          }
        },
      })

      setUploadDrafts(poolResult.drafts)

      if (reviewLaterRef.current || poolResult.cancelled) {
        await discardSparseAndPersistIncompleteSession({
          venueId,
          drafts: poolResult.drafts,
          files: [...filesByDraftId.values()],
          incompleteKind: INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
          discardUploads: discardUnconfirmedUploads,
        })
        refreshIncompleteCount()
        if (!isNotDispatchInvoices) {
          await refreshInvoiceList()
        }
        resetConfirmModalState()
        return
      }

      const sessionFiles =
        selectedUploadFilesRef.current.length > 0
          ? [...selectedUploadFilesRef.current]
          : [...sessionFilesSnapshot]
      const mergedSession = mergeSameInvoiceSessionPages({
        drafts: poolResult.drafts,
        files: sessionFiles,
      })

      // Keep every uploaded file visible — never discard siblings / sparse OCR
      // in the background. User fills details or removes each image themselves.
      setUploadDrafts(mergedSession.drafts)
      commitSelectedUploadFiles(mergedSession.files)
      setFocusId(
        pickFocusAfterUploadReviewAction(mergedSession.drafts, focusId),
      )

      const sparseCount = mergedSession.files.filter(
        isSparseUploadOcrFile,
      ).length
      const anyFailed = mergedSession.drafts.some(
        d => d.status === UPLOAD_DRAFT_STATUS.FAILED,
      )
      const progressLine =
        formatUploadQueueProgressLine(mergedSession.drafts) ||
        (anyFailed ? UPLOAD_QUEUE_COPY.ocrFailed : '')
      const sparseLine =
        sparseCount > 0
          ? `${sparseCount} file${sparseCount === 1 ? '' : 's'} need${
              sparseCount === 1 ? 's' : ''
            } details — fill them in or remove them.`
          : ''
      setOcrBanner(
        [progressLine, mergedSession.mergeBanner, sparseLine]
          .filter(Boolean)
          .join(' · '),
      )
    } catch (error) {
      console.error(error)
      const reason =
        error?.response?.data?.message ||
        error?.message ||
        'The server could not accept these files.'
      closeConfirmModalWithFailure({
        message: 'Review uploads closed — upload failed',
        description: `${reason} The upload window was closed so you can pick files again.`,
      })
    } finally {
      setUploading(false)
      setOcrActive(false)
      clearFileInput()
    }
  }

  const handleFileChange = async event => {
    const picked = Array.from(event.target.files || [])
    clearFileInput()

    if (!picked.length) return

    if (isUploadLimitReached || remainingUploadQuota === 0) {
      openInvoiceUploadPaywall()
      return
    }

    const gate = await promptUploadPickPreflight({
      selectedCount: picked.length,
      remainingQuota: remainingUploadQuota,
    })

    if (!gate.proceed) {
      if (gate.reason === 'quota_exhausted') {
        openInvoiceUploadPaywall()
      }
      return
    }

    const files = gate.useFirstN
      ? takeFirstNPickerFiles(picked, gate.allowedCount)
      : picked

    const validTypes = ['image/jpeg', 'image/png', 'application/pdf']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))
    if (invalidFiles.length > 0) {
      RejectedNotification({
        message: 'Unsupported file type',
        description: 'Please select JPEG, PNG, or PDF files.',
      })
      return
    }

    const oversizedFiles = files.filter(
      file => file.size > MAX_INVOICE_FILE_BYTES,
    )
    if (oversizedFiles.length > 0) {
      RejectedNotification({
        message: 'Invoice file is too large',
        description: `Rejected: ${oversizedFiles
          .map(file => file.name)
          .join(
            ', ',
          )}. Maximum size is 4 MB per file. Please select smaller files and try again.`,
      })
      return
    }

    const sessionDrafts = createUploadSessionDrafts(files)
    setUploadDrafts(sessionDrafts)

    const promises = files.map((file, index) => {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = loadEvent => {
          resolve(
            applyUploadPrefillToFile(
              {
                ...buildEmptyUploadFileFields(
                  file.name,
                  loadEvent.target.result,
                ),
                draftId: sessionDrafts[index]?.id,
                pickIndex: index,
              },
              resolvedPrefill,
            ),
          )
        }
        reader.readAsDataURL(file)
      })
    })

    try {
      const invoices = await Promise.all(promises)
      commitSelectedUploadFiles(invoices)
      setConfirmModal(true)
      setFocusId(sessionDrafts[0]?.id || null)
      setUserPickedFocus(false)

      let suppliers = venueSuppliers.filter(
        supplier => supplier.supplier != null,
      )
      if (suppliers.length === 0) {
        await dispatch(getVenueSuppliers(venueId))
        try {
          const fetched = await API.get('api', `venue/${venueId}/suppliers`)
          suppliers = (fetched || []).filter(
            supplier =>
              supplier &&
              supplier.nonOMSSupplier !== true &&
              supplier.isActive !== false &&
              supplier.supplier != null,
          )
        } catch (error) {
          console.error('Failed to load suppliers for OCR match', error)
        }
      }
      setInitialSuppilers(suppliers)
      await runUploadAndOcr(invoices, sessionDrafts, suppliers)
    } catch (error) {
      console.error('Error converting files to base64:', error)
      const reason =
        error?.response?.data?.message ||
        error?.message ||
        'The selected files could not be prepared for upload.'
      closeConfirmModalWithFailure({
        message: 'Review uploads closed — could not read files',
        description: `${reason} The upload window was closed so you can pick files again.`,
      })
    }
  }

  const removeAttachedInvoiceIds = invoiceIds => {
    if (!setAttachInvoices || !invoiceIds.length) return

    const idSet = new Set(invoiceIds.map(id => String(id)))
    setAttachInvoices(curr =>
      (curr || []).filter(invoiceId => !idSet.has(String(invoiceId))),
    )
  }

  const discardOneUnconfirmedUpload = async invoiceId => {
    try {
      return await discardUnconfirmedUploadedInvoice(invoiceId, venueId)
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || ''
      if (!/only unconfirmed upload drafts can be discarded/i.test(message)) {
        throw error
      }
      // OCR auto-enrich wrote fields before Confirm. Stamp the draft
      // unconfirmed, then discard — covers Review-later rows from before
      // uploadConfirmed existed.
      await enrichUploadedInvoice(invoiceId, {
        uploadConfirmed: false,
        supplierConfirmedByUser: false,
      })
      return discardUnconfirmedUploadedInvoice(invoiceId, venueId)
    }
  }

  const discardUnconfirmedUploads = async (
    files,
    { refreshLibrary = true } = {},
  ) => {
    const invoiceIds = [
      ...new Set(
        files
          .map(file => file?.invoiceId)
          .filter(invoiceId => invoiceId != null && invoiceId !== ''),
      ),
    ]

    if (!invoiceIds.length) return

    const results = await Promise.all(
      invoiceIds.map(invoiceId => discardOneUnconfirmedUpload(invoiceId)),
    )
    restoreLocalUploadQuota(
      countSuccessfulDiscardResults(results, invoiceIds.length),
    )
    removeAttachedInvoiceIds(invoiceIds)
    if (refreshLibrary && !isNotDispatchInvoices) {
      await refreshInvoiceList()
    }
  }

  /** Review later — persist unfinished drafts; keep reading remaining files. */
  const handleReviewLater = async () => {
    if (confirmLoading || discardLoading || uploading) return

    if (ocrActive) {
      reviewLaterRef.current = true
      setOcrBanner('Reading remaining files in the background…')
      SuccessNotification({
        message: 'Still reading your other files',
        description:
          'We’ll finish processing every invoice you picked — you can keep working.',
      })
      resetConfirmModalState()
      return
    }

    await discardSparseAndPersistIncompleteSession({
      venueId,
      drafts: uploadDrafts,
      files: selectedFiles,
      incompleteKind: INCOMPLETE_UPLOAD_KIND.REVIEW_LATER,
      discardUploads: discardUnconfirmedUploads,
    })
    refreshIncompleteCount()
    if (!isNotDispatchInvoices) {
      await refreshInvoiceList()
    }
    resetConfirmModalState()
  }

  const handleDiscardRemaining = () => {
    if (confirmLoading || discardLoading || uploading) return

    Modal.confirm({
      title: UPLOAD_QUEUE_COPY.discardRemaining,
      content: UPLOAD_QUEUE_COPY.discardRemainingConfirm,
      okText: UPLOAD_QUEUE_COPY.discardRemainingOk,
      cancelText: UPLOAD_QUEUE_COPY.discardRemainingCancel,
      centered: true,
      onOk: async () => {
        if (ocrActive) {
          ocrCancelRef.current = true
          reviewLaterRef.current = false
        }
        setDiscardLoading(true)
        try {
          const toDiscard = selectedFiles.filter(file => {
            const draft = findUploadReviewDraft(uploadDrafts, file.draftId)
            return (
              draft &&
              draft.status !== UPLOAD_DRAFT_STATUS.CONFIRMED &&
              draft.status !== UPLOAD_DRAFT_STATUS.DISCARDED
            )
          })
          await discardUnconfirmedUploads(toDiscard)
          removeIncompleteUploads(
            venueId,
            toDiscard.map(f => f.invoiceId).filter(Boolean),
          )
          refreshIncompleteCount()
          SuccessNotification({
            message:
              toDiscard.length === 1 ? 'Upload discarded' : 'Uploads discarded',
            description:
              toDiscard.length === 1
                ? 'The unconfirmed upload was removed.'
                : `${toDiscard.length} unconfirmed uploads were removed.`,
          })
          resetConfirmModalState()
        } catch (error) {
          console.error('Could not discard remaining uploads:', error)
          RejectedNotification({
            message: 'Could not discard uploads',
            description:
              error?.response?.data?.message ||
              error?.message ||
              'Please try again.',
          })
        } finally {
          setDiscardLoading(false)
        }
      },
    })
  }

  const handleConfirmAll = async () => {
    let items = listUploadReviewConfirmableItems(uploadDrafts, selectedFiles)
    if (!canConfirmAllUploadReview(uploadDrafts, selectedFiles)) return
    if (confirmLoading || discardLoading || uploading) return

    for (const { file, draft } of [...items]) {
      if (typeof beforeConfirmUpload === 'function') {
        try {
          const decision = await beforeConfirmUpload([file])
          if (decision === 'use_existing') {
            await handleRemoveFocus(draft.id)
            items = items.filter(
              item => String(item.draft.id) !== String(draft.id),
            )
            continue
          }
          if (decision === false) return
        } catch (error) {
          console.error('beforeConfirmUpload failed', error)
          return
        }
      } else {
        const duplicateDecision = await promptUploadDuplicateWarn({
          uploadFile: file,
          libraryInvoices: uploadedInvoices || [],
        })
        if (duplicateDecision === 'use_existing') {
          await handleRemoveFocus(draft.id)
          items = items.filter(
            item => String(item.draft.id) !== String(draft.id),
          )
          continue
        }
        if (duplicateDecision === 'abort') return
      }
    }

    if (!items.length) return
    if (
      !items.every(({ file, draft }) =>
        canConfirmUploadReviewFocus(file, draft),
      )
    ) {
      return
    }

    const confirmIds = new Set(items.map(({ draft }) => String(draft.id)))
    setConfirmLoading(true)
    const confirmingDrafts = uploadDrafts.map(d =>
      confirmIds.has(String(d.id)) ? markUploadDraftConfirming(d) : d,
    )
    setUploadDrafts(confirmingDrafts)

    try {
      await enrichUploadedInvoices(
        items.map(({ file }) => ({
          invoiceId: file.invoiceId,
          body: buildEnrichPayloadFromUploadFile(file),
        })),
      )

      if (!isNotDispatchInvoices) {
        await refreshInvoiceList()
      }

      const confirmedInvoiceIds = items
        .map(({ file }) => file.invoiceId)
        .filter(Boolean)
      if (confirmedInvoiceIds.length) {
        removeIncompleteUploads(venueId, confirmedInvoiceIds)
        refreshIncompleteCount()
      }

      const nextDrafts = confirmingDrafts.map(d =>
        confirmIds.has(String(d.id)) ? markUploadDraftConfirmed(d) : d,
      )
      setUploadDrafts(nextDrafts)

      if (typeof onConfirmed === 'function') {
        await onConfirmed()
      }
      await finishSession()
    } catch (error) {
      console.error(error)
      setUploadDrafts(prev =>
        prev.map(d =>
          confirmIds.has(String(d.id)) &&
          d.status === UPLOAD_DRAFT_STATUS.CONFIRMING
            ? transitionUploadDraft(d, UPLOAD_DRAFT_STATUS.READY)
            : d,
        ),
      )
      RejectedNotification({
        message: 'Could not save document details',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleRemoveFocus = async draftId => {
    const file = findUploadReviewFile(selectedFiles, draftId)
    const draft = findUploadReviewDraft(uploadDrafts, draftId)
    if (!draft || draft.status === UPLOAD_DRAFT_STATUS.CONFIRMED) return
    if (discardLoading || confirmLoading || uploading) return

    try {
      // Keep the review UI visible — no full-modal loading or library refresh.
      if (file) {
        await discardUnconfirmedUploads([file], { refreshLibrary: false })
      }
      if (file?.invoiceId) {
        removeIncompleteUploads(venueId, [file.invoiceId])
        refreshIncompleteCount()
      }
      const nextDrafts = uploadDrafts.map(d =>
        String(d.id) === String(draftId) ? markUploadDraftDiscarded(d) : d,
      )
      setUploadDrafts(nextDrafts)
      commitSelectedUploadFiles(prev =>
        prev.filter(f => String(f.draftId) !== String(draftId)),
      )
      setUserPickedFocus(false)

      const outcome = getUploadReviewRemoveOutcome(nextDrafts, draftId)
      setFocusId(outcome.nextFocusId)
      SuccessNotification({
        message: 'Upload removed',
        description: outcome.sessionEmpty
          ? 'No uploads left in this session.'
          : 'Review the remaining uploads in the session.',
      })
    } catch (error) {
      console.error('Could not remove upload:', error)
      RejectedNotification({
        message: 'Could not remove upload',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    }
  }

  const openIncompleteResume = async () => {
    if (confirmModal || uploading || confirmLoading || discardLoading) return

    let library = uploadedInvoices || []
    // Full library walk only when parent did not opt into page refresh.
    if (
      !isNotDispatchInvoices &&
      typeof onRefreshInvoices !== 'function' &&
      !library.length
    ) {
      const refreshed = await dispatch(getUploadedInvoices(venueId))
      if (Array.isArray(refreshed)) library = refreshed
    }

    const { drafts, files } = buildResumeSessionFromIncompleteList({
      venueId,
      libraryInvoices: library,
    })
    if (!drafts.length) {
      refreshIncompleteCount(library)
      return
    }

    // skipped → ready so they are editable again on resume
    const resumedDrafts = drafts.map(d =>
      d.status === UPLOAD_DRAFT_STATUS.SKIPPED
        ? transitionUploadDraft(d, UPLOAD_DRAFT_STATUS.READY)
        : d,
    )

    setUploadDrafts(resumedDrafts)
    commitSelectedUploadFiles(files)
    setFocusId(pickFocusAfterUploadReviewAction(resumedDrafts, null))
    setUserPickedFocus(false)
    setOcrBanner(UPLOAD_QUEUE_COPY.resumeTrayLabel(resumedDrafts.length))
    setConfirmModal(true)
    refreshIncompleteCount(library)

    let suppliers = venueSuppliers.filter(supplier => supplier.supplier != null)
    if (suppliers.length === 0) {
      await dispatch(getVenueSuppliers(venueId))
      try {
        const fetched = await API.get('api', `venue/${venueId}/suppliers`)
        suppliers = (fetched || []).filter(
          supplier =>
            supplier &&
            supplier.nonOMSSupplier !== true &&
            supplier.isActive !== false &&
            supplier.supplier != null,
        )
      } catch (error) {
        console.error('Failed to load suppliers for resume', error)
      }
    }
    setInitialSuppilers(suppliers)
  }

  const handleRetryOcr = async draftId => {
    const draft = findUploadReviewDraft(uploadDrafts, draftId)
    const existingFile = findUploadReviewFile(selectedFiles, draftId)
    if (
      !existingFile ||
      !draft ||
      draft.status !== UPLOAD_DRAFT_STATUS.FAILED
    ) {
      return
    }
    if (uploading || discardLoading || confirmLoading) return

    let nextDraft = transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.READING)
    setUploadDrafts(prev =>
      prev.map(d => (String(d.id) === String(draftId) ? nextDraft : d)),
    )
    commitSelectedUploadFiles(prev =>
      prev.map(f =>
        String(f.draftId) === String(draftId)
          ? { ...f, ocrStatus: 'pending', ocrError: null, ocrWarnings: [] }
          : f,
      ),
    )

    const file =
      selectedUploadFilesRef.current.find(
        f => String(f.draftId) === String(draftId),
      ) || existingFile
    const result = await runOcrForDraft(nextDraft, file, initialSuppleirs)
    if (result.ok) {
      nextDraft = transitionUploadDraft(nextDraft, UPLOAD_DRAFT_STATUS.READY, {
        error: null,
      })
      commitSelectedUploadFiles(prev =>
        prev.map(f =>
          String(f.draftId) === String(draftId)
            ? mergeOcrResultPreservingUploadFileEdits(f, result.file)
            : f,
        ),
      )
    } else {
      nextDraft = transitionUploadDraft(nextDraft, UPLOAD_DRAFT_STATUS.FAILED, {
        error: result.error,
      })
      if (result.file) {
        commitSelectedUploadFiles(prev =>
          prev.map(f =>
            String(f.draftId) === String(draftId)
              ? mergeOcrResultPreservingUploadFileEdits(f, result.file)
              : f,
          ),
        )
      }
    }
    setUploadDrafts(prev => {
      const updated = prev.map(d =>
        String(d.id) === String(draftId) ? nextDraft : d,
      )
      setOcrBanner(formatUploadQueueProgressLine(updated))
      return updated
    })
  }

  const updateFileByDraftId = (draftId, updater) => {
    commitSelectedUploadFiles(prev =>
      prev.map(f => (String(f.draftId) === String(draftId) ? updater(f) : f)),
    )
  }

  // Keep focus following ready-first when OCR completes (unless user jumped).
  useEffect(() => {
    if (!confirmModal || userPickedFocus) return
    setFocusId(prev => pickFocusAfterUploadReviewAction(uploadDrafts, prev))
  }, [uploadDrafts, confirmModal, userPickedFocus])

  return (
    <div>
      {showLimitModal && (
        <PlanLimitModal
          showLimitModal={showLimitModal}
          setShowLimitModal={setShowLimitModal}
          variant='invoiceUploadLimit'
        />
      )}
      {invoiceUploadUsage.usageLine ? (
        <UploadUsageLine>{invoiceUploadUsage.usageLine}</UploadUsageLine>
      ) : null}
      {isUploadLimitReached ? (
        <UploadLimitBanner role='status'>
          <UploadLimitTitle>
            {UPLOAD_PLAN_LIMIT_COPY.limitReachedTitle}
          </UploadLimitTitle>
          <UploadLimitBody>
            {invoiceUploadUsage.limitReachedBody}
          </UploadLimitBody>
          <Button type='primary' onClick={openInvoiceUploadPaywall}>
            {UPLOAD_PLAN_LIMIT_COPY.upgradeCta}
          </Button>
        </UploadLimitBanner>
      ) : null}
      <UploadTriggerWrap>
        <Button
          style={{
            background: isUploadLimitReached ? '#d9d9d9' : '#b5d922',
            width: '100%',
            borderColor: isUploadLimitReached ? '#d9d9d9' : undefined,
            color: isUploadLimitReached ? 'rgba(0, 0, 0, 0.45)' : undefined,
          }}
          type='primary'
          disabled={isUploadLimitReached || ocrActive || uploading}
          icon={isUploadLimitReached ? <LockOutlined /> : undefined}
          onClick={() => {
            if (isUploadLimitReached || ocrActive || uploading) {
              notifyUploadTriggerBlocked()
              return
            }
            fileInputRef.current?.click()
          }}
        >
          {triggerLabel}
        </Button>
      </UploadTriggerWrap>
      {showIncompleteTray ? (
        <IncompleteUploadsTray
          count={incompleteCount}
          onResume={openIncompleteResume}
          disabled={confirmModal || uploading || discardLoading}
          cleanupBanner={cleanupBanner}
          onDismissBanner={() => setCleanupBanner('')}
        />
      ) : null}
      <input
        type='file'
        id='fileInput'
        accept='.jpg,.jpeg,.png,image/jpeg,image/jpg,image/png,application/pdf'
        style={{ display: 'none' }}
        multiple
        ref={fileInputRef}
        disabled={isUploadLimitReached}
        onChange={handleFileChange}
        onClick={event => {
          if (isUploadLimitReached) {
            event.preventDefault()
          }
        }}
      />

      <Modal
        width={960}
        title='Review uploads'
        visible={confirmModal}
        footer={null}
        closable={!uploading && !confirmLoading && !discardLoading}
        maskClosable={false}
        onCancel={handleReviewLater}
        destroyOnClose
      >
        {isLoading || uploading || discardLoading ? (
          <LoadingWrapper>
            <Loading />
            {ocrBanner && <OcrStatusText>{ocrBanner}</OcrStatusText>}
          </LoadingWrapper>
        ) : (
          <ReviewUploadsPanel
            drafts={uploadDrafts}
            files={selectedFiles}
            venueId={venueId}
            venueSuppliers={initialSuppleirs}
            progressLine={ocrBanner}
            ocrActive={ocrActive}
            focusId={focusId}
            onFocusIdChange={setFocusId}
            userPickedFocus={userPickedFocus}
            onUserPickFocus={setUserPickedFocus}
            confirmLoading={confirmLoading}
            actionsDisabled={uploading || discardLoading}
            currentPlan={currentPlan}
            venueCount={venueCount}
            isAdmin={Boolean(isAdmin)}
            onConfirmAll={handleConfirmAll}
            canConfirmAll={canConfirmAllUploadReview(
              uploadDrafts,
              selectedFiles,
            )}
            confirmAllLabel={getConfirmAllUploadReviewLabel(
              uploadDrafts,
              selectedFiles,
            )}
            onRemoveFocus={handleRemoveFocus}
            onRetryOcr={handleRetryOcr}
            onReviewLater={handleReviewLater}
            onDiscardRemaining={handleDiscardRemaining}
            onUpdateSupplier={(draftId, supplierId, title) =>
              updateFileByDraftId(draftId, f =>
                touchUploadFileField(
                  touchUploadFileField(
                    {
                      ...f,
                      supplier: supplierId,
                      supplierTitle: title,
                    },
                    'supplier',
                  ),
                  'supplierTitle',
                ),
              )
            }
            onCreateSupplierFromUpload={async (draftId, supplierName) => {
              try {
                const result = await createManualVenueSupplierFromUpload({
                  venueId,
                  supplierName,
                })
                const vs = result.venueSupplier
                updateFileByDraftId(draftId, f =>
                  touchUploadFileField(
                    touchUploadFileField(
                      {
                        ...f,
                        supplier: vs._id,
                        supplierTitle: vs.supplierTitle || supplierName,
                      },
                      'supplier',
                    ),
                    'supplierTitle',
                  ),
                )
                await dispatch(getVenueSuppliers(venueId))
                setInitialSuppilers(prev => {
                  const list = Array.isArray(prev) ? prev : []
                  if (list.some(item => String(item._id) === String(vs._id))) {
                    return list
                  }
                  return [
                    {
                      _id: vs._id,
                      supplier: vs.supplier,
                      supplierTitle: vs.supplierTitle || supplierName,
                      isActive: true,
                      manualOrdersOnly: true,
                    },
                    ...list,
                  ]
                })
                SuccessNotification({
                  message: result.created
                    ? CREATE_SUPPLIER_FROM_UPLOAD_COPY.successCreated
                    : CREATE_SUPPLIER_FROM_UPLOAD_COPY.successReused,
                })
              } catch (error) {
                RejectedNotification({
                  message: 'Could not create supplier',
                  description:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Please try again or add them under Your Suppliers.',
                })
                throw error
              }
            }}
            onUpdateField={(draftId, field, value) =>
              updateFileByDraftId(draftId, f =>
                touchUploadFileField({ ...f, [field]: value }, field),
              )
            }
            onUpdateTotal={(draftId, value) =>
              updateFileByDraftId(draftId, f =>
                touchUploadFileField(
                  {
                    ...f,
                    invoiceTotalInput: value,
                    invoiceTotalSource: INVOICE_TOTAL_SOURCE.MANUAL,
                  },
                  'invoiceTotalInput',
                ),
              )
            }
            onUseEstimate={draftId =>
              updateFileByDraftId(draftId, f =>
                touchUploadFileField(
                  applyInvoiceTotalEstimate(f),
                  'invoiceTotalInput',
                ),
              )
            }
            onUpdatePurchaseOrder={(draftId, poUpdate) => {
              commitSelectedUploadFiles(prev => {
                const index = prev.findIndex(
                  f => String(f.draftId) === String(draftId),
                )
                if (index < 0) return prev
                const next = applyPurchaseOrderUpdate(prev, index, poUpdate)
                return next.map((f, i) =>
                  i === index
                    ? applyManualPurchaseOrderTouchToUploadFile(f, poUpdate)
                    : f,
                )
              })
            }}
            previewZoomByDraftId={previewZoomByDraftId}
            onPreviewZoomChange={(draftId, value) =>
              setPreviewZoomByDraftId(prev => ({
                ...prev,
                [String(draftId)]: value,
              }))
            }
          />
        )}
      </Modal>
    </div>
  )
}

export default AddInvoices

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
`

const OcrStatusText = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
`

const UploadTriggerWrap = styled.div`
  width: 100%;
`

const UploadUsageLine = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`

const UploadLimitBanner = styled.div`
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  border-radius: 8px;
`

const UploadLimitTitle = styled.p`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const UploadLimitBody = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`
